import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, List, ListItemButton, ListItemText,
  Typography, Divider, Stack, Button, Container, TextField, MenuItem, Chip
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import BreadcrumbsContainer from '../../containers/common/breadcurmbs/BreadcrumbsContainer';
import * as carApi from '../../lib/api/car';
const CarAssetListContainer = () => {
  const [cars, setCars] = useState([]);
  const [selectedId, setSelectedId] = useState(cars[0]?.id);
  const [editData, setEditData] = useState(cars[0]);
  const [isAdding, setIsAdding] = useState(false);

  const fetchCarAssets = async () => {
    try {
      const response = await carApi.getCarAssetList();
      // 서버에서 Map 형태 { "list": [...] }로 내려주므로 .list로 접근
      const data = response.data.list || []; 
      setCars(data);

      // 데이터가 있으면 첫 번째 항목 자동 선택 (목록이 비어있지 않을 때만)
      if (data.length > 0) {
        handleSelect(data[0]);
      }
    } catch (error) {
      console.error("데이터 로드 실패:", error);
    }
  };

  // ✅ 페이지 진입 시 최초 1회 실행
  useEffect(() => {
    fetchCarAssets();
  }, []);

  const handleSelect = (car) => {
    setIsAdding(false);
    setSelectedId(car.carUuid);
    setEditData({
      carUuid: car.carUuid || '',
      carNumber: car.carNumber || '',
      carModel: car.carModel || '',
      rentStartDate: car.rentStartDate || '',
      rentEndDate: car.rentEndDate || '',
      statusYn: car.statusYn || 'Y',
      memo: car.memo || '',
      parking: car.parking || ''
    });
  };

  const handleToggleAdd = () => {
    if (isAdding) {
      const lastSelected = cars.find(c => c.id === selectedId) || cars[0];
      if (lastSelected) {
        setEditData({ ...lastSelected });
        setSelectedId(lastSelected.id);
      }
      setIsAdding(false);
    } else {
      setIsAdding(true);
      setSelectedId(null);
      setEditData({
        carUuid: '',
        carNumber: '',
        carModel: '',
        rentStartDate: '',
        rentEndDate: '',
        statusYn: '',
        memo: '',
        parking: '',
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const saveData = editData;
    
    if (!saveData.carNumber) return alert('차량번호를 입력해주세요.');
    if (!saveData.carModel) return alert('차종을 입력해주세요.');

    const isDuplicate = cars.some(car => 
      (isAdding ? true : car.carUuid !== selectedId) && 
      car.carNumber.trim() === saveData.carNumber.trim()
    );

    if (isDuplicate) {
      return alert('이미 등록된 차량번호입니다. 다시 확인해주세요.');
    }

    const dataToSubmit = {
      ...saveData,
      rentStartDate: saveData.rentStartDate === "" ? null : saveData.rentStartDate,
      rentEndDate: saveData.rentEndDate === "" ? null : saveData.rentEndDate,
    };

    try {
      if (isAdding) {
        const response = await carApi.registerCarAsset(dataToSubmit); 
        const newCar = response.data; 
        
        setCars(prev => [newCar, ...prev]);
        setSelectedId(newCar.id);
        setIsAdding(false);
        alert('신규 차량이 등록되었습니다.');
      } else {
        await carApi.updateCarAsset(dataToSubmit);
        
        setCars(prev => prev.map(c => c.id === selectedId ? dataToSubmit : c));
        alert('차량 정보가 저장되었습니다.');
      }
      await fetchCarAssets(); 
    } catch (error) {
      console.error("저장 실패:", error);
      alert(error.response?.data?.message || '저장 중 오류가 발생했습니다.');
    }
  };


  const handleDelete = async () => {
    if (!selectedId) return;

    if (window.confirm('해당 차량 정보를 삭제하시겠습니까?')) {
      try {
        await carApi.deleteCarAsset(selectedId);

        const filtered = cars.filter(c => c.carUuid !== selectedId); 
        setCars(filtered);

        if (filtered.length > 0) {
          handleSelect(filtered[0]);
        } else {
          setSelectedId(null);
          setEditData({
            carUuid: '',
            carNumber: '',
            carModel: '',
            rentStartDate: '',
            rentEndDate: '',
            statusYn: '',
            memo: '',
            parking: ''
          });
        }
        alert('삭제되었습니다.');
      } catch (error) {
        console.error("삭제 실패:", error);
        alert(error.response?.data?.message || '삭제 중 오류가 발생했습니다.');
      }
    }
  };


  return (
    <>
      <BreadcrumbsContainer title="총무 관리 / 차량 관리 / 리스트" icon="common" />
      <Box sx={{ minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>차량 자산 관리</Typography>
            <Button
              variant={isAdding ? "outlined" : "contained"}
              color={isAdding ? "error" : "primary"}
              startIcon={isAdding ? <CloseIcon /> : <AddIcon />}
              onClick={handleToggleAdd}
              sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}
            >
              {isAdding ? "취소" : "신규 차량 등록"}
            </Button>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ height: '680px', borderRadius: 3, overflow: 'hidden', border: '1px solid #E0E4E8' }}>
                <List sx={{ p: 0 }}>
                  {cars.map((car) => (
                    <ListItemButton
                      key={car.carUuid}
                      selected={!isAdding && selectedId === car.carUuid}
                      onClick={() => handleSelect(car)}
                      sx={{
                        py: 2, px: 2,
                        borderBottom: '1px solid #F0F0F0',
                        '&.Mui-selected': { bgcolor: '#F5F9FF', borderLeft: '4px solid #1976D2' }
                      }}
                    >
                      <DirectionsCarIcon sx={{ mr: 1.5, fontSize: 20, color: !isAdding && selectedId === car.id ? 'primary.main' : '#B0B8C1' }} />
                      <ListItemText
                        primary={car.carNumber}
                        secondary={car.carModel}
                        primaryTypographyProps={{ fontWeight: 700, variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ArrowForwardIosIcon sx={{ fontSize: 12, color: '#D1D9E0' }} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper variant="outlined" sx={{ height: '680px', p: 4, borderRadius: 3, border: '1px solid #E0E4E8' }}>
                {editData ? (
                  <Stack spacing={4}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isAdding ? 'primary.main' : 'inherit' }}>
                        {isAdding ? "⚡ 신규 차량 정보 입력" : "차량 상세 정보 편집"}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {!isAdding && (
                          <Chip 
                            label={editData.statusYn === 'S' ? "운행 중" : (editData.statusYn === 'Y' ? "운행 가능" : "사용 중지")} 
                            color={editData.statusYn === 'S' ? "info" : (editData.statusYn === 'Y' ? "success" : "error")}
                            variant="filled"
                            sx={{ fontWeight: 800, mr: 1 }}
                          />
                        )}
                        {!isAdding && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            disabled={editData.statusYn === 'S'}
                            onClick={async () => {
                              const isToStop = editData.statusYn === 'Y';
                              if (window.confirm(`차량을 ${isToStop ? '사용 중지' : '운행 가능'} 상태로 변경하시겠습니까?`)) {
                                const statusValue = isToStop ? 'N' : 'Y';
                                editData.statusYn = statusValue;
                                await handleSave();
                              }
                            }}
                            sx={{ 
                              fontWeight: 700, 
                              borderColor: '#ccc',
                              "&.Mui-disabled": {
                                borderColor: '#f0f0f0',
                                color: '#bdbdbd'
                              }
                            }}
                          >
                            {editData.statusYn === 'S' ? "변경 불가" : (editData.statusYn === 'Y' ? "중지 처리" : "정상 전환")}
                          </Button>
                        )}

                        <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSave} sx={{ fontWeight: 700 }}>
                          {isAdding ? "등록 완료" : "저장"}
                        </Button>
                        {!isAdding && (
                          <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDelete} sx={{ fontWeight: 700 }}>삭제</Button>
                        )}
                      </Stack>
                    </Stack>

                    <Divider />
                    <Grid container rowSpacing={3} columnSpacing={2}>
                      <Grid item xs={6}>
                        <TextField fullWidth
                          label="차량번호"
                          name="carNumber"
                          value={editData.carNumber||''}
                          onChange={handleChange}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="차종"
                          name="carModel"
                          value={editData.carModel||''}
                          onChange={handleChange}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth label="렌트 시작일" name="rentStartDate" type="date"
                          value={editData.rentStartDate||''} onChange={handleChange}
                          size="small" InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth label="렌트 종료일" name="rentEndDate" type="date"
                          value={editData.rentEndDate||''} onChange={handleChange}
                          size="small" InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="차량 상태"
                          name="status"
                          value={
                            editData.statusYn === 'Y' ? '운행 가능' : 
                            editData.statusYn === 'S' ? '운행 중' : 
                            editData.statusYn === 'N' ? '수리 중' : ''
                          }
                          size="small"
                          InputProps={{
                            readOnly: true, 
                          }}
                        />

                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          select // Select 박스로 설정
                          label="주차위치"
                          name="parking" // formik이나 state에서 관리하는 키값에 맞게 수정하세요
                          value={editData.parking || ''} // 데이터 바인딩
                          onChange={handleChange}
                          size="small"
                          // SelectProps를 통해 내부 메뉴 스타일 조정 가능
                          SelectProps={{
                            native: false,
                          }}
                        >
                          <MenuItem value="B1">B1</MenuItem>
                          <MenuItem value="B2">B2</MenuItem>
                          <MenuItem value="B3">B3</MenuItem>
                          <MenuItem value="B4">B4</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth multiline rows={7}
                          label="비고 및 특이사항" name="memo"
                          value={editData.memo||''} onChange={handleChange}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                ) : (
                  <Stack sx={{ height: '100%' }} justifyContent="center" alignItems="center">
                    <Typography variant="body2" color="text.secondary">목록에서 차량을 선택하거나 신규 차량을 등록해 주세요.</Typography>
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default CarAssetListContainer;
